/**
 * LeaveDialog — Add/edit leave entries (vakantie, ziekte, bijzonder, etc.)
 * Uses werkrooster to determine hours per day when available.
 */

import { useState } from 'react';
import { format } from 'date-fns';
import type { LeaveType, LeaveEntry, LeaveEntitlement, WerkroosterDay } from '../../stores/workforce-store';
import { calculateNettoHours } from '../../lib/hours-calculator';
import { LEAVE_TYPES } from '../../lib/leave-types';

interface LeaveDialogProps {
  staffName: string;
  staffId: string;
  werkrooster?: WerkroosterDay[] | null;
  shiftType?: string | null; // for break calculation
  existing?: LeaveEntry | null;
  entitlements?: LeaveEntitlement[];
  leaveEntries?: LeaveEntry[];
  onSave: (data: { staff_id: string; date: string; leave_type: LeaveType; hours: number; notes?: string }) => Promise<boolean>;
  onDelete?: (id: string) => Promise<boolean>;
  onClose: () => void;
}

// Leave types imported from central config

function getRoosterHours(werkrooster: WerkroosterDay[] | null | undefined, dayOfWeek: number, shiftType: string | null): number | null {
  if (!werkrooster) return null;
  const entry = werkrooster.find(w => w.day === dayOfWeek);
  if (!entry) return null;
  return calculateNettoHours(entry.from, entry.to, shiftType);
}

export function LeaveDialog({ staffName, staffId, werkrooster, shiftType, existing, entitlements, leaveEntries, onSave, onDelete, onClose }: LeaveDialogProps) {
  const hasRooster = werkrooster && werkrooster.length > 0;
  const [leaveType, setLeaveType] = useState<LeaveType>(existing?.leave_type || 'vakantie');
  const [dateFrom, setDateFrom] = useState(existing?.date || format(new Date(), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(existing?.date || format(new Date(), 'yyyy-MM-dd'));
  const [manualHours, setManualHours] = useState(existing?.hours?.toString() || '8');
  const [useRooster, setUseRooster] = useState(!!hasRooster);
  const [notes, setNotes] = useState(existing?.notes || '');
  const [isSaving, setIsSaving] = useState(false);

  // Find matching entitlement (potje) for selected leave type
  const matchingEntitlement = entitlements?.find(ent => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return ent.leave_type === leaveType && ent.valid_until >= today;
  });
  const entitlementUsed = matchingEntitlement && leaveEntries
    ? leaveEntries
        .filter(l => l.leave_type === matchingEntitlement.leave_type && l.date >= matchingEntitlement.valid_from && l.date <= matchingEntitlement.valid_until)
        .reduce((sum, l) => sum + l.hours, 0)
    : 0;
  const entitlementRemaining = matchingEntitlement ? matchingEntitlement.total_hours - entitlementUsed : null;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const from = new Date(dateFrom + 'T00:00:00');
      const to = new Date(dateTo + 'T00:00:00');
      const fallbackHours = parseFloat(manualHours) || 8;

      for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
        const dayOfWeek = d.getDay();

        // Determine hours for this day
        let hours: number;
        if (useRooster && werkrooster) {
          const roosterHours = getRoosterHours(werkrooster, dayOfWeek, shiftType || null);
          if (roosterHours === null || roosterHours <= 0) continue; // skip non-werkrooster days
          hours = roosterHours;
        } else {
          // Skip weekends (unless feestdag)
          if (leaveType !== 'feestdag' && (dayOfWeek === 0 || dayOfWeek === 6)) continue;
          hours = fallbackHours;
        }

        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const success = await onSave({
          staff_id: staffId,
          date: dateStr,
          leave_type: leaveType,
          hours,
          notes: notes || undefined,
        });
        if (!success) break;
      }
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  // Preview: show which days will be created with what hours
  const previewDays: { date: string; dayLabel: string; hours: number }[] = [];
  if (dateFrom && dateTo) {
    const from = new Date(dateFrom + 'T00:00:00');
    const to = new Date(dateTo + 'T00:00:00');
    const dayNames = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
    const fallbackHours = parseFloat(manualHours) || 8;
    for (let d = new Date(from); d <= to && previewDays.length < 30; d.setDate(d.getDate() + 1)) {
      const dow = d.getDay();
      if (useRooster && werkrooster) {
        const h = getRoosterHours(werkrooster, dow, shiftType || null);
        if (h !== null && h > 0) {
          previewDays.push({
            date: `${d.getDate()} ${['jan','feb','mrt','apr','mei','jun','jul','aug','sep','okt','nov','dec'][d.getMonth()]}`,
            dayLabel: dayNames[dow]!,
            hours: h,
          });
        }
      } else {
        if (leaveType !== 'feestdag' && (dow === 0 || dow === 6)) continue;
        previewDays.push({
          date: `${d.getDate()} ${['jan','feb','mrt','apr','mei','jun','jul','aug','sep','okt','nov','dec'][d.getMonth()]}`,
          dayLabel: dayNames[dow]!,
          hours: fallbackHours,
        });
      }
    }
  }
  const totalPreviewHours = previewDays.reduce((s, d) => s + d.hours, 0);

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-main mb-1">
          {existing ? 'Verlof bewerken' : 'Verlof toevoegen'}
        </h3>
        <p className="text-sm text-muted mb-5">{staffName}</p>

        {/* Leave type buttons */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          {LEAVE_TYPES.map(lt => (
            <button
              key={lt.value}
              onClick={() => setLeaveType(lt.value)}
              className={`py-2 rounded-lg text-xs font-medium border transition-colors ${
                leaveType === lt.value ? lt.color : 'bg-white border-gray-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {lt.label}
            </button>
          ))}
        </div>

        {/* Potje indicator */}
        {matchingEntitlement && entitlementRemaining !== null && (
          <div className={`mb-4 p-3 rounded-lg border text-sm ${
            entitlementRemaining > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center justify-between">
              <span className="font-medium">
                Potje: {matchingEntitlement.description || matchingEntitlement.leave_type}
              </span>
              <span className={`font-semibold ${entitlementRemaining > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                {Math.round(entitlementRemaining * 10) / 10}u beschikbaar
              </span>
            </div>
            <div className="mt-1.5 h-2 bg-white/50 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${entitlementRemaining > 0 ? 'bg-emerald-500' : 'bg-red-500'}`}
                style={{ width: `${Math.min(100, (entitlementUsed / matchingEntitlement.total_hours) * 100)}%` }}
              />
            </div>
            <p className="text-xs mt-1 opacity-70">
              {Math.round(entitlementUsed * 10) / 10}u opgenomen van {matchingEntitlement.total_hours}u
              {totalPreviewHours > 0 && entitlementRemaining - totalPreviewHours < 0 && (
                <span className="text-red-600 font-medium"> — overschrijdt potje met {Math.round((totalPreviewHours - entitlementRemaining) * 10) / 10}u</span>
              )}
            </p>
          </div>
        )}

        {/* Date range */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Van</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => {
                setDateFrom(e.target.value);
                if (e.target.value > dateTo) setDateTo(e.target.value);
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Tot en met</label>
            <input
              type="date"
              value={dateTo}
              min={dateFrom}
              onChange={e => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        {/* Hours mode: werkrooster or manual */}
        {hasRooster && (
          <div className="flex items-center gap-3 mb-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={useRooster}
                onChange={e => setUseRooster(e.target.checked)}
                className="rounded border-gray-300"
              />
              Uren op basis van werkrooster
            </label>
          </div>
        )}

        {!useRooster && (
          <div className="mb-4">
            <label className="block text-xs font-medium text-muted mb-1">Uren per dag</label>
            <input
              type="number"
              step="0.5"
              min="0"
              max="24"
              value={manualHours}
              onChange={e => setManualHours(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
        )}

        {/* Preview */}
        {previewDays.length > 0 && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs font-medium text-muted mb-2">
              {previewDays.length} {previewDays.length === 1 ? 'dag' : 'dagen'} · totaal {Math.round(totalPreviewHours * 10) / 10}u
            </p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {previewDays.map((d, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-muted">{d.dayLabel} {d.date}</span>
                  <span className="text-main font-medium">{Math.round(d.hours * 10) / 10}u</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="mb-5">
          <label className="block text-xs font-medium text-muted mb-1">Notitie</label>
          <input
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Optioneel"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={isSaving || previewDays.length === 0}
            className="flex-1 py-2.5 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 disabled:opacity-50 transition-colors"
          >
            {isSaving ? 'Opslaan...' : `Opslaan (${previewDays.length} ${previewDays.length === 1 ? 'dag' : 'dagen'})`}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-slate-600 hover:bg-slate-100 rounded-lg text-sm transition-colors"
          >
            Annuleren
          </button>
          {existing && onDelete && (
            <button
              onClick={() => onDelete(existing.id)}
              disabled={isSaving}
              className="px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg text-sm transition-colors disabled:opacity-50"
            >
              Verwijder
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
